import { AxiosError } from "axios";
import { CreditCard, DollarSign } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import useAuthStore from "@/store/auth";
import env from "@/valid-env";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";

export const Route = createFileRoute("/test-payment")({
	loader: async () => {
		const user = useAuthStore.getState().user;

		const userType = user?.userType === "BUYER" ? "buyer" : "vendor";
		if (env.VITE_SAFEHAVEN_ENV === "production")
			throw redirect({ to: `/${userType}/home` });
	},
	component: PaymentForm
});

const formSchema = z.object({
	accountNumber: z
		.string()
		.min(5, {
			message: "Account number must be at least 5 characters."
		})
		.max(20, {
			message: "Account number must not exceed 20 characters."
		}),
	amount: z.string().refine(
		(val) => {
			const num = Number.parseFloat(val);
			return !isNaN(num) && num > 0;
		},
		{
			message: "Amount must be a positive number."
		}
	)
});

function usePayVFD() {
	return useMutation({
		mutationFn: async (values: z.infer<typeof formSchema>) => {
			const token = localStorage.getItem("vfd-session");

			if (!token)
				throw new Error("Invalid access token, please create a deposit or withdrawal transaction");
			try {
				const response = await fetch("https://api-devapps.vfdbank.systems/vtech-wallet/api/v1.1/wallet2/credit", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"AccessToken": token
					},
					body: JSON.stringify({
						...values,
						accountNo: values.accountNumber,
						senderAccountNo: "5050104057",
						senderBank: "999070",
						senderNarration: "Test credit"
					})
				});

				if (!response.ok)
					throw new Error(await response.text());
				const data = await response.json();
				return data;
			}
			catch (error) {
				void error;
				throw error;
			}
		}
	});
}

export default function PaymentForm() {
	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			accountNumber: "",
			amount: ""
		}
	});
	const payVFD = usePayVFD();

	function onSubmit(values: z.infer<typeof formSchema>) {
		toast.promise(payVFD.mutateAsync(values), {
			loading: "Processing payment...",
			success: () => {
				form.reset();
				return "Payment successful";
			},
			error: (error) => {
				if (error instanceof AxiosError)
					return `Payment failed: ${error.response?.data?.message || "Please try again."}`;
				return `Payment failed: ${error.message || "Please try again."}`;
			}
		});
	}

	return (
		<div className="flex h-screen items-center justify-center">
			<Card className="w-full max-w-md mx-auto">
				<CardHeader>
					<CardTitle className="text-2xl">Make a Payment</CardTitle>
					<CardDescription>Enter the account details and payment amount.</CardDescription>
				</CardHeader>
				<CardContent>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
							<FormField
								control={form.control}
								name="accountNumber"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Account Number</FormLabel>
										<FormControl>
											<div className="relative">
												<CreditCard className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
												<Input className="pl-10" placeholder="Enter account number" {...field} />
											</div>
										</FormControl>
										<FormDescription>Enter the account number you wish to pay.</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="amount"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Payment Amount</FormLabel>
										<FormControl>
											<div className="relative">
												<DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
												<Input className="pl-10" placeholder="0.00" {...field} />
											</div>
										</FormControl>
										<FormDescription>Enter the amount you wish to pay.</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
							<Button type="submit" className="w-full" disabled={payVFD.isPending}>
								Make Payment
							</Button>
						</form>
					</Form>
				</CardContent>
				<CardFooter className="flex justify-between text-sm text-muted-foreground">
					<p>Secure payment processing</p>
					<p>All transactions are encrypted</p>
				</CardFooter>
			</Card>
		</div>
	);
}
